import PropTypes from "prop-types";
import { useTheme } from "@/hooks/ThemeContext";

const Button = ({
  text,
  onClick = () => {},
  icon,
  className = "",
  disabled = false,
  isActive = false,
  color = "",
  customBgColor = "",
  customTextColor = "",
  iconPosition = "right",
  rounded = "md",
  fontWeight = "semibold",
  ...props
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const hasTextAndIcon = text && icon;

  // Remove font weight from base classes and handle it separately with inline styles
  const baseClasses = `flex items-center px-6 py-5 rounded-${rounded} transition-colors duration-300`;
  const justifyClass =
    iconPosition === "right" && hasTextAndIcon
      ? "justify-between"
      : "justify-center";

  // Create dynamic styles for higher specificity
  const dynamicStyle = {
    fontWeight: getFontWeightValue(fontWeight), // Convert semantic weight to numeric
  };

  if (isDarkMode && (color === "primary" || customBgColor === "primary")) {
    // Apply white text with high specificity via inline styles
    dynamicStyle.color = "#FFFFFF";
  }

  let colorClasses;
  if (customBgColor && customTextColor) {
    colorClasses = `bg-${customBgColor} ${
      isDarkMode && customBgColor === "primary" ? "" : `text-${customTextColor}`
    }`;
  } else if (disabled) {
    colorClasses = "bg-darkBlue text-primary";
  } else if (isActive) {
    colorClasses = `bg-${color} text-white hover:bg-${color}-600`;
  } else if (color === "primary") {
    colorClasses = `bg-secondary ${isDarkMode ? "" : "text-primary"}`;
  } else if (color === "darkBlue") {
    colorClasses = "bg-darkBlue text-primary";
  } else if (color === "outline") {
    colorClasses = `bg-transparent text-red-500 hover:bg-red-100 border-red-500 border-2`;
  } else {
    colorClasses = `bg-${color} text-white hover:bg-${color}-600`;
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${justifyClass} ${colorClasses} ${className}`}
      style={dynamicStyle}
      {...props}>
      {iconPosition === "left" && icon && <span className="mr-2">{icon}</span>}
      {text && <span>{text}</span>}
      {iconPosition === "right" && icon && <span className="ml-2">{icon}</span>}
    </button>
  );
};

function getFontWeightValue(weight) {
  const weightMap = {
    thin: 100,
    extralight: 200,
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
    black: 900,
  };

  return weightMap[weight] || 400; // Default to 400 (normal) if not found
}

Button.propTypes = {
  text: PropTypes.string,
  onClick: PropTypes.func,
  icon: PropTypes.element,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  isActive: PropTypes.bool,
  color: PropTypes.string,
  customBgColor: PropTypes.string,
  customTextColor: PropTypes.string,
  iconPosition: PropTypes.oneOf(["left", "right"]),
  rounded: PropTypes.string,
  fontWeight: PropTypes.string,
};

export default Button;
